import { useAuth } from '@/contexts/AuthContext';
import ErrorState from '@/components/common/ErrorState';
import SalesPerformanceSummary from '@/components/sales/SalesPerformanceSummary';
import PermissionGate from '@/components/common/PermissionGate';
// ─────────────────────────────────────────────
// KLOTHIQ — Dashboard Page (Premium Dark + Charts)
// ─────────────────────────────────────────────

import { useDashboardRange } from '@/hooks/useDashboardRange';
import DashboardPeriod from '@/components/common/DashboardPeriod';
import DashboardQuickMenu from '@/components/dashboard/DashboardQuickMenu';
import Link from '@/components/common/AccessLink';
import PageHeader from '@/components/common/PageHeader';
import { SkeletonCard } from '@/components/common/LoadingState';
import StatusBadge from '@/components/common/StatusBadge';
import { useDashboardKPIs, useRecentSales } from '@/hooks/useDashboard';
import { useSalesAnalytics } from '@/hooks/useSalesAnalytics';
import { useLowStock } from '@/hooks/useInventory';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  ShoppingCart,
  Package,
  TrendingUp,
  AlertTriangle,
  ArrowUpRight,
  Clock,
  IndianRupee,
  BarChart3,
  TrendingDown,
  Landmark,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/* ═══════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════ */

function formatRupee(val: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
}

function shortSku(sku: string): string {
  const parts = sku.split('-');
  return parts.length <= 2 ? sku : parts.slice(-2).join('-');
}

/* ═══════════════════════════════════════════════════
   TIMEFRAME OPTIONS & MINI SPARKLINE
═══════════════════════════════════════════════════ */

function Sparkline({ data, color }: { data: { v: number }[]; color: string }) {
  if (!data || data.length === 0) return null;
  const safeId = `spark-${color.replace(/[^a-zA-Z0-9]/g, '')}`;
  return (
    <ResponsiveContainer width="100%" height={44}>
      <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={safeId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="v"
          stroke={color}
          strokeWidth={1.5}
          fill={`url(#${safeId})`}
          dot={false}
          animationDuration={800}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* ═══════════════════════════════════════════════════
   METRIC CARD
═══════════════════════════════════════════════════ */

type CardVariant = 'teal' | 'emerald' | 'amber' | 'purple' | 'blue' | 'neutral';

const CARD_VARIANTS: Record<
  CardVariant,
  {
    accentClass: string;
    iconBg: string;
    iconColor: string;
    sparkColor: string;
    trendColor: string;
  }
> = {
  teal: {
    accentClass: 'card-accent-teal',
    iconBg: 'bg-cyan-500/10',
    iconColor: 'text-cyan-400',
    sparkColor: '#06B6D4',
    trendColor: 'text-cyan-400',
  },
  emerald: {
    accentClass: 'card-accent-emerald',
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-400',
    sparkColor: '#10B981',
    trendColor: 'text-emerald-400',
  },
  amber: {
    accentClass: 'card-accent-amber',
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-400',
    sparkColor: '#F59E0B',
    trendColor: 'text-amber-400',
  },
  purple: {
    accentClass: 'card-accent-purple',
    iconBg: 'bg-indigo-500/10',
    iconColor: 'text-indigo-400',
    sparkColor: '#818CF8',
    trendColor: 'text-indigo-400',
  },
  blue: {
    accentClass: 'card-accent-blue',
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-400',
    sparkColor: '#3B82F6',
    trendColor: 'text-blue-400',
  },
  neutral: {
    accentClass: 'card-accent-neutral',
    iconBg: 'bg-white/5',
    iconColor: 'text-muted-foreground',
    sparkColor: '#94A3B8',
    trendColor: 'text-muted-foreground',
  },
};

interface MetricCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  subtitle?: string;
  variant?: CardVariant;
  trend?: { data: { v: number }[]; label?: string; up?: boolean };
}

function MetricCard({ title, value, icon, subtitle, variant = 'neutral', trend }: MetricCardProps) {
  const s = CARD_VARIANTS[variant];
  return (
    <div
      className={cn(
        'glass-card group flex flex-col gap-3 p-4 transition-all duration-200',
        s.accentClass,
      )}
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            {title}
          </p>
          <p className="mt-1.5 text-xl font-bold tracking-tight text-foreground sm:text-2xl">
            {value}
          </p>
          {subtitle && <p className="mt-0.5 text-[11px] text-muted-foreground">{subtitle}</p>}
          {trend?.label && (
            <p className={cn('mt-1 flex items-center gap-1 text-[11px] font-medium', s.trendColor)}>
              {trend.up === true ? (
                <TrendingUp className="h-3 w-3" />
              ) : trend.up === false ? (
                <TrendingDown className="h-3 w-3" />
              ) : null}
              {trend.label}
            </p>
          )}
        </div>
        <div className={cn('shrink-0 rounded-xl p-2.5', s.iconBg, s.iconColor)}>{icon}</div>
      </div>
      {/* Sparkline */}
      {trend && (
        <div className="mt-auto -mx-1">
          <Sparkline data={trend.data} color={s.sparkColor} />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   CUSTOM CHART TOOLTIP
═══════════════════════════════════════════════════ */

function ChartTooltip({ active, payload, label }: any) {
  const { can } = useAuth();
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  const sales = Number(point?.sales ?? 0),
    profit = Number(point?.profit ?? 0);
  const financial = can('financials.view') && point?.profit !== undefined;
  return (
    <div className="min-w-[170px] space-y-2 rounded-xl border border-border bg-card/95 p-3 text-xs shadow-xl">
      <p className="font-bold">{point?.fullLabel || label}</p>
      <p className="flex justify-between gap-4">
        <span>Invoice value</span>
        <strong>{formatRupee(sales)}</strong>
      </p>
      {financial && (
        <>
          <p className="flex justify-between gap-4">
            <span>Invoice profit</span>
            <strong>{formatRupee(profit)}</strong>
          </p>
          <p className="flex justify-between gap-4">
            <span>Margin</span>
            <strong>{(sales > 0 ? (profit / sales) * 100 : 0).toFixed(1)}%</strong>
          </p>
        </>
      )}
      <p>{point?.ordersCount ?? 0} orders</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════ */

export default function DashboardPage() {
  const { can } = useAuth();
  const period = useDashboardRange();
  const range = period.range;
  const {
    data: kpis,
    isLoading: kpisLoading,
    error: kpisError,
    refetch: reloadKpis,
  } = useDashboardKPIs(range);
  const {
    data: analytics,
    isLoading: analyticsLoading,
    error: analyticsError,
    refetch: reloadAnalytics,
  } = useSalesAnalytics(range);
  const monthlyAnalytics = analytics;
  const {
    data: recentSales,
    isLoading: salesLoading,
    error: salesError,
    refetch: reloadRecent,
  } = useRecentSales(range);
  const {
    data: lowStock,
    isLoading: stockLoading,
    error: stockError,
    refetch: reloadStock,
  } = useLowStock();

  return (
    <div className="space-y-5">
      <PageHeader title="Dashboard" description="Business overview at a glance" />
      <DashboardQuickMenu />
      <DashboardPeriod
        range={range}
        today={period.today}
        onApply={period.apply}
        onMonth={period.month}
        onReset={period.reset}
      />
      {period.invalid && (
        <p role="alert" className="text-sm text-amber-600">
          Invalid saved dates. Showing the current business month; choose a valid range above.
        </p>
      )}

      {/* ── Row 1: Sales KPIs ── */}
      {kpisLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : kpisError ? (
        <ErrorState message={kpisError.message} onRetry={() => reloadKpis()} />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <MetricCard
            title="Invoice Sales"
            value={formatRupee(kpis?.sales_this_month ?? 0)}
            icon={<ShoppingCart className="h-5 w-5" />}
            subtitle="Selected period · before return adjustments"
            variant="emerald"
            trend={
              monthlyAnalytics?.sparklineSales?.length
                ? {
                    data: monthlyAnalytics.sparklineSales,
                    label: 'Trajectory',
                    up:
                      (monthlyAnalytics.sparklineSales.at(-1)?.v ?? 0) >=
                      (monthlyAnalytics.sparklineSales[0]?.v ?? 0),
                  }
                : undefined
            }
          />
          <MetricCard
            title="Collections Received"
            value={formatRupee(kpis?.collected_this_month ?? 0)}
            icon={<IndianRupee className="h-5 w-5" />}
            subtitle="Selected period · gross receipts by payment date"
            variant="teal"
            trend={
              monthlyAnalytics?.sparklineSales?.length
                ? {
                    data: monthlyAnalytics.sparklineReceipts,
                    label: 'Receipts by payment date',
                  }
                : undefined
            }
          />
          <MetricCard
            title="Refunds paid"
            value={formatRupee(kpis?.refunds_this_month ?? 0)}
            icon={<TrendingDown className="h-5 w-5" />}
            subtitle="Selected period · actual refund date"
            variant="amber"
          />
          <PermissionGate permission="financials.view">
            <MetricCard
              title="Invoice Profit"
              value={formatRupee(kpis?.gross_profit_this_month ?? 0)}
              icon={<TrendingUp className="h-5 w-5" />}
              subtitle="Selected period · before return adjustments"
              variant="purple"
              trend={
                monthlyAnalytics?.sparklineProfit?.length
                  ? {
                      data: monthlyAnalytics.sparklineProfit,
                      label: `${(monthlyAnalytics?.grossMarginPercent ?? 0).toFixed(0)}% Margin`,
                      up:
                        (monthlyAnalytics.sparklineProfit.at(-1)?.v ?? 0) >=
                        (monthlyAnalytics.sparklineProfit[0]?.v ?? 0),
                    }
                  : undefined
              }
            />
          </PermissionGate>
        </div>
      )}

      {!kpisError && kpis && (
        <p className="text-xs text-muted-foreground">
          {range.from} to {range.to} in the organization timezone. Refunds paid in this period:{' '}
          {formatRupee(kpis.refunds_this_month)}. Updated{' '}
          {new Date(kpis.as_of).toLocaleString('en-IN')}. Current balances and stock are independent
          of the selected activity period.
        </p>
      )}
      {kpis?.purchase_value !== undefined && (
        <section className="grid gap-3 sm:grid-cols-2" aria-label="Purchases in selected period">
          <MetricCard
            title="Purchase value"
            value={formatRupee(kpis.purchase_value)}
            icon={<ShoppingCart className="h-5 w-5" />}
            subtitle="Selected period · purchase date"
          />
          <MetricCard
            title="Supplier payments"
            value={formatRupee(kpis.supplier_payments ?? 0)}
            icon={<Landmark className="h-5 w-5" />}
            subtitle="Selected period · actual payment date"
          />
        </section>
      )}
      <h2 className="text-sm font-semibold">
        Current position{' '}
        <span className="font-normal text-muted-foreground">· balances and stock as of now</span>
      </h2>
      {/* ── Row 2: Stock KPIs ── */}
      {kpisLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : kpisError ? null : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <MetricCard
            title="Receivable"
            value={formatRupee(kpis?.total_receivable ?? 0)}
            icon={<Clock className="h-5 w-5" />}
            subtitle="Current · excluding returns under review"
            variant="amber"
          />
          {can('financials.view') && kpis?.total_cash_balance !== undefined && (
            <>
              {' '}
              <MetricCard
                title="Cash & Bank Balance"
                value={formatRupee(kpis?.total_cash_balance ?? 0)}
                icon={<Landmark className="h-5 w-5" />}
                subtitle="Available funds (includes sales)"
                variant="teal"
              />{' '}
            </>
          )}

          <MetricCard
            title="Stock on Hand"
            value={`${kpis?.total_stock_on_hand ?? 0} units`}
            icon={<Package className="h-5 w-5" />}
            subtitle="Total inventory"
            variant="blue"
          />
          <MetricCard
            title="Reserved"
            value={`${kpis?.total_stock_reserved ?? 0} units`}
            icon={<Package className="h-5 w-5" />}
            subtitle="Active stock reservations"
            variant="neutral"
          />
          <MetricCard
            title="Low Stock"
            value={`${kpis?.low_stock_count ?? 0} variants`}
            icon={<AlertTriangle className="h-5 w-5" />}
            subtitle="Needs attention"
            variant={(kpis?.low_stock_count ?? 0) > 0 ? 'amber' : 'neutral'}
          />
        </div>
      )}

      {Number(kpis?.return_review_count ?? 0) > 0 && (
        <p
          role="status"
          className="rounded-xl border border-amber-500/30 p-3 text-sm text-amber-600"
        >
          {kpis?.return_review_count} returned orders need a balance review. Their due is excluded
          from the receivable total.
        </p>
      )}

      <SalesPerformanceSummary range={range} />
      {/* ── Row 3: Sales Chart + Panels ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Sales Trend Chart (2/3 width) */}
        <div className="glass-card flex flex-col justify-between p-5 lg:col-span-2">
          {/* Header */}
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-foreground">Sales Overview</h3>
                <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                  Updated every minute
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {range.from} to {range.to} · invoice values before return adjustments
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Legend */}
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-cyan-400" />
                  Sales
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-indigo-400" />
                  Profit
                </span>
              </div>
            </div>
          </div>

          {/* Quick Period Summary Ribbon */}
          {!analyticsLoading && !analyticsError && analytics && (
            <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 rounded-xl bg-muted/30 border border-border/40 p-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Period Invoice Sales
                </p>
                <p className="text-sm font-bold text-foreground mt-0.5">
                  {formatRupee(analytics?.totalSales ?? 0)}
                </p>
              </div>
              <PermissionGate permission="financials.view">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Invoice Profit
                  </p>
                  <p className="text-sm font-bold text-emerald-500 mt-0.5">
                    {formatRupee(analytics?.totalProfit ?? 0)}
                  </p>
                </div>
              </PermissionGate>
              {can('financials.view') && analytics?.totalProfit !== undefined && (
                <>
                  {' '}
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Gross Margin
                    </p>
                    <p className="text-sm font-bold text-foreground mt-0.5">
                      {(analytics?.grossMarginPercent ?? 0).toFixed(1)}%
                    </p>
                  </div>
                </>
              )}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Volume
                </p>
                <p className="text-sm font-bold text-primary mt-0.5">
                  {analytics?.totalOrders ?? 0} orders
                </p>
              </div>
            </div>
          )}
          {/* Recharts Area Chart */}
          <div className="h-[280px] w-full pt-1">
            {analyticsError ? (
              <ErrorState message={analyticsError.message} onRetry={() => void reloadAnalytics()} />
            ) : analyticsLoading ? (
              <div className="flex h-full items-center justify-center">
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  <span className="text-xs">Loading analytics...</span>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={analytics?.chartData ?? []}
                  margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="grad-sales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#06B6D4" stopOpacity={0.01} />
                    </linearGradient>
                    <linearGradient id="grad-profit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#818CF8" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#818CF8" stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border) / 0.6)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`)}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ stroke: 'hsl(var(--muted-foreground) / 0.25)', strokeWidth: 1 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    name="sales"
                    stroke="#06B6D4"
                    strokeWidth={2.5}
                    fill="url(#grad-sales)"
                    dot={{ r: 2.5, fill: '#06B6D4', strokeWidth: 0 }}
                    activeDot={{ r: 5.5, stroke: 'hsl(var(--background))', strokeWidth: 2 }}
                    animationDuration={600}
                  />
                  {can('financials.view') && analytics?.totalProfit !== undefined && (
                    <>
                      {' '}
                      <Area
                        type="monotone"
                        dataKey="profit"
                        name="profit"
                        stroke="#818CF8"
                        strokeWidth={2.5}
                        fill="url(#grad-profit)"
                        dot={{ r: 2.5, fill: '#818CF8', strokeWidth: 0 }}
                        activeDot={{ r: 5.5, stroke: 'hsl(var(--background))', strokeWidth: 2 }}
                        animationDuration={800}
                      />
                    </>
                  )}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Right panel: Recent Sales */}
        <div className="glass-card overflow-hidden">
          <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-bold text-foreground">Recent Sales</h3>
            </div>
            <Link
              to={`/sales?from=${range.from}&to=${range.to}`}
              className="flex items-center gap-1 text-[11px] font-medium text-primary hover:opacity-80 transition-opacity"
            >
              All <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>

          {!can('sales.view') ? (
            <p className="p-4 text-sm text-muted-foreground">
              Sales access is required to view recent orders.
            </p>
          ) : salesError ? (
            <ErrorState message={salesError.message} onRetry={() => void reloadRecent()} />
          ) : salesLoading ? (
            <div className="space-y-3 p-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex justify-between animate-pulse">
                  <div className="h-3.5 w-28 rounded bg-white/8" />
                  <div className="h-3.5 w-14 rounded bg-white/5" />
                </div>
              ))}
            </div>
          ) : !recentSales?.length ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10 px-4 text-center">
              <ShoppingCart className="h-9 w-9 text-muted-foreground/20" />
              <p className="text-xs text-muted-foreground">No sales recorded yet</p>
              <Link
                to="/sales/new"
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary/15 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/25 transition-colors"
              >
                Record First Sale <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {recentSales.map((sale) => (
                <Link
                  key={sale.id as string}
                  to={`/sales/${sale.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.03] transition-colors"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold text-foreground">
                      {(sale.customer_name as string | null) ||
                        (sale.customers as { name: string } | null)?.name ||
                        'Walk-in Customer'}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {sale.sale_number as string}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Seller: {(sale.seller_name as string) || 'Seller not recorded'}
                    </p>
                  </div>
                  <div className="ml-2 shrink-0 text-right">
                    <p className="text-xs font-bold text-foreground">
                      {formatRupee(Number(sale.total_amount))}
                    </p>
                    <StatusBadge status={sale.sale_status as string} size="sm" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Row 4: Low Stock ── */}
      <div className="glass-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-white/5 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <h3 className="text-sm font-bold text-foreground">Low Stock Alerts</h3>
            {(lowStock?.length ?? 0) > 0 && (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-400 border border-amber-500/20">
                {lowStock!.length}
              </span>
            )}
          </div>
          <Link
            to="/inventory"
            className="flex items-center gap-1 text-[11px] font-medium text-primary hover:opacity-80 transition-opacity"
          >
            View Inventory <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>

        {stockLoading ? (
          <p role="status" className="p-4 text-sm">
            Loading stock alerts…
          </p>
        ) : stockError ? (
          <ErrorState message={stockError.message} onRetry={() => reloadStock()} />
        ) : !lowStock?.length ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Package className="h-5 w-5 text-emerald-400/60" />
            All variants are well stocked 🎉
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {lowStock.slice(0, 6).map((item) => (
              <div
                key={item.variant_id}
                className="flex items-center justify-between px-5 py-3 hover:bg-white/[0.02] transition-colors"
              >
                <div className="min-w-0 flex-1 pr-4">
                  <p
                    className="truncate text-sm font-semibold text-foreground"
                    title={item.product_name}
                  >
                    {item.product_name}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    <span className="font-mono">{shortSku(item.sku)}</span>
                    {item.size ? ` · ${item.size}` : ''}
                    {item.color ? ` / ${item.color}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className={cn(
                      'text-sm font-bold',
                      item.quantity_available <= 0 ? 'text-red-400' : 'text-amber-400',
                    )}
                  >
                    {item.quantity_available} left
                  </span>
                  <StatusBadge status={item.stock_status} size="sm" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
